import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  ApproveLoanRequestHGO,
  RejectLoanRequestHGO,
  ApproveLoanRequestMonazam,
  RejectLoanRequestMonazam,
  GetLoanRequests,
  GetMonazamLoanRequests,
  GetHGODetails,
  GetSameMonazamHGOs,
} from "../../../../Actions/RequestActions";
import { SuccessToast, WarningToast, formatAmount } from "../../../../Util";
import { FaDownload, FaUpload, FaTimes } from "react-icons/fa";
import "./LoanRequestsTable.css";

const formatDateTime = (date, time) => {
  if (!date) return '';
  const [year, month, day] = date.split('T')[0].split('-');
  const formattedDate = `${day}/${month}/${year}`;
  
  if (time) {
    const [hours, minutes] = time.split(':');
    const hour24 = parseInt(hours, 10);
    const ampm = hour24 >= 12 ? 'P.M' : 'A.M';
    const displayHours = hour24 % 12 || 12;
    const displayMinutes = minutes.padStart(2, '0');
    return `${formattedDate} (${displayHours}:${displayMinutes} ${ampm})`;
  }
  
  return formattedDate;
};

const ApproveRejectModal = ({ isOpen, onClose, request, onAction, userType, isApproving }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
  };

  const handleFileClick = () => {
    fileRef.current?.click();
  };

  const handleSubmit = () => {
    if (isApproving && userType === "hgo" && !file) {
      WarningToast("Letter of concern is required when approving.");
      return;
    }

    const formData = new FormData();
    if (userType === "hgo") {
      formData.append('giver_status', isApproving ? 'approve' : 'reject');
      if (isApproving && file) {
        formData.append('borrower_letter_of_concern', file);
      }
    } else {
      formData.append('monazam_status', isApproving ? 'approve' : 'reject');
    }

    setLoading(true);
    onAction(request.id, formData, () => {
      setLoading(false);
    }, () => {
      setLoading(false);
      setFile(null);
      onClose();
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block modal-blur-bg" onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isApproving ? 'Approve' : 'Reject'} Loan Request
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {isApproving && userType === "hgo" && (
              <div className="mb-3">
                <label className="form-label">Letter of Concern *</label>
                <div className="file-upload-area">
                  <input
                    type="file"
                    ref={fileRef}
                    className="file-input d-none"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                  <label className="file-upload-label" onClick={handleFileClick}>
                    <FaUpload className="upload-icon" />
                    <span>{file ? file.name : 'Upload'}</span>
                  </label>
                </div>
              </div>
            )}
            <p>
              Are you sure you want to {isApproving ? 'approve' : 'reject'} this loan request?
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              type="button"
              className={`btn ${isApproving ? 'btn-success' : 'btn-danger'}`}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Processing...' : (isApproving ? 'Approve' : 'Reject')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoanRequestsTable = ({ reload, setReload, page, setPage, setTotalPages }) => {
  const { userType } = useParams();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [currentUserExtendedId, setCurrentUserExtendedId] = useState(null);
  const [hgos, setHGOs] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    if (userType === "hgo") {
      const userId = localStorage.getItem("id");
      setCurrentUserId(userId);
      
      GetHGODetails(
        (data) => {
          if (data?.id) {
            setCurrentUserExtendedId(data.id);
          }
        },
        () => {},
        () => {}
      );
      
      GetSameMonazamHGOs(setHGOs, () => {}, () => {});
    }
  }, [userType]);

  const fetchLoanRequests = async () => {
    setLoading(true);
    setError("");
    if (userType === "monazam") {
      GetMonazamLoanRequests(
        setData,
        setLoading,
        setError,
        page,
        setTotalPages,
        "pending"
      );
    } else {
      GetLoanRequests(
        setData,
        setLoading,
        setError,
        page,
        setTotalPages,
        "pending"
      );
    }
  };

  useEffect(() => {
    fetchLoanRequests();
  }, [page, reload, userType]);

  useEffect(() => {
    if (userType === "hgo" && data.length > 0) {
      const filtered = data.filter((loan) => {
        const isSender = (loan.sender?.id === currentUserId) || (loan.senderId === currentUserId) || (loan.sender_id === currentUserId);
        const isBorrower = currentUserExtendedId && 
          ((loan.borrower?.id === currentUserExtendedId) || (loan.borrowerId === currentUserExtendedId) || (loan.borrower_id === currentUserExtendedId));
        return isSender || isBorrower;
      });
      setFilteredData(filtered);
    } else if (userType === "monazam") {
      setFilteredData(data);
    } else {
      setFilteredData([]);
    }
  }, [data, currentUserExtendedId, currentUserId, userType]);

  const handleApprove = async () => {
    SuccessToast("Loan request approved successfully.");
    await new Promise(resolve => setTimeout(resolve, 300));
    fetchLoanRequests();
    if (setReload) {
      setReload(prev => prev + 1);
    }
  };

  const handleReject = async () => {
    SuccessToast("Loan request rejected successfully.");
    await new Promise(resolve => setTimeout(resolve, 300));
    fetchLoanRequests();
    if (setReload) {
      setReload(prev => prev + 1);
    }
  };

  const approveRequest = (request) => {
    setSelectedRequest(request);
    setApproveModalOpen(true);
  };

  const rejectRequest = (request) => {
    setSelectedRequest(request);
    setRejectModalOpen(true);
  };

  const handleApproveAction = (id, formData, setError, onSuccess) => {
    const successCallback = () => {
      handleApprove();
      if (onSuccess) {
        onSuccess();
      }
    };
    if (userType === "monazam") {
      ApproveLoanRequestMonazam(id, formData, setError, successCallback);
    } else {
      ApproveLoanRequestHGO(id, formData, setError, successCallback);
    }
  };

  const handleRejectAction = (id, formData, setError, onSuccess) => {
    const successCallback = () => {
      handleReject();
      if (onSuccess) {
        onSuccess();
      }
    };
    if (userType === "monazam") {
      RejectLoanRequestMonazam(id, formData, setError, successCallback);
    } else {
      RejectLoanRequestHGO(id, formData, setError, successCallback);
    }
  };

  const getStatusDisplay = (request) => {
    if (userType === "monazam") {
      if (request.monazamStatus === "approve") {
        return <span className="text-success">Approved</span>;
      } else if (request.monazamStatus === "reject") {
        return <span className="text-danger">Rejected</span>;
      } else {
        return <span className="text-warning">Pending</span>;
      }
    } else {
      if (request.giverStatus === "approve" && request.monazamStatus === "approve") {
        return <span className="text-success">Approved</span>;
      } else if (request.giverStatus === "reject" || request.monazamStatus === "reject") {
        return <span className="text-danger">Rejected</span>;
      } else if (request.giverStatus === "approve") {
        return <span className="text-info">Pending Monazam</span>;
      } else {
        return <span className="text-warning">Pending</span>;
      }
    }
  };

  const isCreator = (request) => {
    if (!currentUserId && !currentUserExtendedId) return false;
    return (
      (request.sender?.id === currentUserId) || 
      (request.senderId === currentUserId) || 
      (request.sender_id === currentUserId) ||
      (request.sender?.id === currentUserExtendedId) ||
      (request.senderId === currentUserExtendedId) ||
      (request.sender_id === currentUserExtendedId) ||
      (request.sender?.extendedUserId === currentUserExtendedId) ||
      (request.sender?.userId === currentUserId)
    );
  };

  const getHGOName = (id) => {
    if (!id) return 'N/A';
    // The hgos array from /auth/same-monazam-hgos only has id and name
    // Match by HGO extended id directly
    const hgo = hgos.find(h => h.id === id);
    return hgo?.name || 'N/A';
  };

  const canApprove = (request) => {
    if (userType === "monazam") {
      return request.monazamStatus === "pending" && request.giverStatus === "approve";
    } else {
      if (isCreator(request)) {
        return false;
      }
      return request.giverStatus === "pending";
    }
  };

  const canReject = (request) => {
    if (userType === "monazam") {
      return request.monazamStatus === "pending" && request.giverStatus === "approve";
    } else {
      if (isCreator(request)) {
        return false;
      }
      return request.giverStatus === "pending";
    }
  };


  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center loading-container">
        <Spinner animation="border" className="spinner-green" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  else if (!!error) return <p className="text-danger">{error}</p>;
  else
    return (
      <div>
        <ApproveRejectModal
          isOpen={approveModalOpen}
          onClose={() => {
            setApproveModalOpen(false);
            setSelectedRequest(null);
          }}
          request={selectedRequest}
          onAction={handleApproveAction}
          userType={userType}
          isApproving={true}
        />
        <ApproveRejectModal
          isOpen={rejectModalOpen}
          onClose={() => {
            setRejectModalOpen(false);
            setSelectedRequest(null);
          }}
          request={selectedRequest}
          onAction={handleRejectAction}
          userType={userType}
          isApproving={false}
        />

        <div className="table-responsive loan-requests-table-wrapper">
          <table className="table loan-requests-table">
          <thead>
            <tr>
              <th className="tableheader-txt py-3">Date / Time</th>
                <th className="tableheader-txt py-3">
                  {userType === "monazam" ? "From HGO" : "HGO Name"}
                </th>
                {userType === "monazam" && (
                  <th className="tableheader-txt py-3">To HGO</th>
                )}
              <th className="tableheader-txt py-3">Narration</th>
              <th className="tableheader-txt py-3">Currency</th>
              <th className="tableheader-txt py-3">Amount</th>
                <th className="tableheader-txt py-3">Sender Letter</th>
                {userType === "monazam" && (
                  <th className="tableheader-txt py-3">Borrower Letter</th>
                )}
                {userType === "hgo" ? (
                  <th className="tableheader-txt py-3">Status / Actions</th>
                ) : (
                  <>
              <th className="tableheader-txt py-3">Status</th>
                    <th className="tableheader-txt py-3">Actions</th>
                  </>
                )}
            </tr>
          </thead>
          <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={userType === "monazam" ? 10 : 7} className="text-center py-5 text-muted">
                    No pending loan requests available
                  </td>
                </tr>
              ) : (
                filteredData
                  .sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt))
              .map((request, index) => (
                    <tr key={request.id || index}>
                      <td className="tabledata-txt py-3">
                        {formatDateTime(request.date, request.time)}
                      </td>
                      <td className="tabledata-txt py-3">
                        {userType === "monazam"
                          ? request.fromHgo || request.senderHGO?.name || getHGOName(request.senderId || request.sender_id) || 'N/A'
                          : isCreator(request)
                          ? (request.borrower?.name || getHGOName(request.borrowerId || request.borrower_id || request.borrower?.id))
                          : (request.sender?.name || 'N/A')}
                      </td>
                      {userType === "monazam" && (
                        <td className="tabledata-txt py-3">
                          {request.toHgo || request.borrowerHGO?.name || 'N/A'}
                        </td>
                      )}
                      <td className="tabledata-txt py-3">
                        {request.narration || 'N/A'}
                      </td>
                      <td className="tabledata-txt py-3">
                        {request.currency || 'SAR'}
                      </td>
                <td className="tabledata-txt py-3">
                        {formatAmount(request.amount || 0)}
                </td>
                <td className="tabledata-txt py-3">
                        {request.senderLetterOfConcern || request.sender_letter_of_concern ? (
                          <div className="d-flex justify-content-center">
                            <button
                              className="btn btn-sm download-btn"
                              onClick={() => {
                                const fileUrl = request.senderLetterOfConcern || request.sender_letter_of_concern;
                                if (fileUrl) {
                                  window.open(fileUrl, '_blank');
                                }
                              }}
                              title="Sender Letter of Concern"
                            >
                              <FaDownload className="download-icon" />
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex justify-content-center">
                            <span className="text-muted">No file</span>
                          </div>
                        )}
                </td>
                      {userType === "monazam" && (
                <td className="tabledata-txt py-3">
                          {request.borrowerLetterOfConcern || request.borrower_letter_of_concern ? (
                    <div className="d-flex justify-content-center">
                      <button
                        className="btn btn-sm download-btn"
                        onClick={() => {
                                  const fileUrl = request.borrowerLetterOfConcern || request.borrower_letter_of_concern;
                                  if (fileUrl) {
                          window.open(fileUrl, '_blank');
                                  }
                        }}
                                title="Borrower Letter of Concern"
                      >
                        <FaDownload className="download-icon" />
                      </button>
                    </div>
                  ) : (
                    <div className="d-flex justify-content-center">
                      <span className="text-muted">No file</span>
                    </div>
                  )}
                        </td>
                      )}
                      {userType === "hgo" ? (
                        <td className="tabledata-txt py-3">
                          {isCreator(request) ? (
                            getStatusDisplay(request)
                          ) : canApprove(request) || canReject(request) ? (
                            <div className="d-flex gap-2">
                              {canApprove(request) && (
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => approveRequest(request)}
                                >
                                  Approve
                                </button>
                              )}
                              {canReject(request) && (
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => rejectRequest(request)}
                                >
                                  Reject
                                </button>
                              )}
                            </div>
                          ) : (
                            getStatusDisplay(request)
                          )}
                        </td>
                      ) : (
                        <>
                          <td className="tabledata-txt py-3">
                            {getStatusDisplay(request)}
                </td>
                <td className="tabledata-txt py-3">
                            {canApprove(request) || canReject(request) ? (
                              <div className="d-flex gap-2">
                                {canApprove(request) && (
                                  <button
                                    className="btn btn-sm btn-success"
                                    onClick={() => approveRequest(request)}
                                  >
                                    Approve
                                  </button>
                                )}
                                {canReject(request) && (
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => rejectRequest(request)}
                                  >
                                    Reject
                                  </button>
                                )}
                              </div>
                  ) : (
                              <span className="text-muted">-</span>
                  )}
                </td>
                        </>
                      )}
              </tr>
                  ))
              )}
          </tbody>
        </table>
        </div>
      </div>
    );
};

export default LoanRequestsTable;
